/**
 * 「系统参数运维」页的各配置项组件。
 *
 * 三种形态，按「值的体量」而不是「重要程度」区分：
 *
 * 1. ``ConfigRow`` —— 单行值（URL / 模板 / 版本号）。行式列表，默认只读展示，
 *    点「编辑」就地展开输入框。原实现给这四项也各发一张 ~120px 高的卡片，
 *    和 500px+ 的 JSON 卡挤在同一个 auto-fill 网格里，行高差四倍把网格撑得七零八落。
 * 2. ``ConfigSwitchRow`` / ``ConfigSelectRow`` —— 值域封闭的布尔开关 / 枚举下拉。
 *    控件本身就是编辑态，所以不套「编辑」这一步，但仍要按「保存」才落库
 *    （原因见各自 docstring）。
 * 3. ``BazelPresetsCard`` / ``ModuleRegistryCard`` / ``EvaluatorWhitelistCard``
 *    —— JSON 值。全宽面板 + 「表单 / JSON」双视图（右上角 ``Segmented`` 切换）：
 *    表单视图用 ``KvTableEditor`` 按行编辑（30+ 条评测器靠它自带的搜索才找得到），
 *    JSON 视图用 ``JsonEditor``（行号 + 高亮 + 实时校验）。两个视图底层共用同一份
 *    draft 字符串，切换不丢改动；JSON 不合法时保存按钮直接 disable，
 *    不再等点了保存才弹一句 message。
 *
 * 所有本地校验都走 ``./validators``，与后端 ``_validate_*`` 同口径。
 *
 * ── 三条贯穿全文件的写法约定，改之前务必读 ────────────────────────
 *
 * A. **受控编辑器的 value 必须是组件自己持有的 state，不能是 useMemo 派生量。**
 *    React 19 的 restoreControlledState 会在 onChange 之后把 DOM 里的字符按
 *    ``value`` prop 回滚；如果 onChange 因为解析失败而没更新 ``value`` 的来源，
 *    用户敲的字会**当场消失**，而且是静默的。``ModuleRegistryCard`` 的 JSON 视图
 *    以前正是这样（value 是三字段派生的 memo，onChange 解析失败就空 catch），
 *    结果是：结构编不动、高亮层冻住、实时校验失效、保存时把**旧值**写进 Redis
 *    还弹成功提示。现在 JSON 视图持有自己的 ``jsonDraft``，逐字回存原文，
 *    解析成功才**额外**回写三字段。
 *
 * B. **每张卡只有一个根节点。** loading / error / 正常三态都在同一个
 *    ``Panel`` / ``.sc-row`` 里切内容，不做「config 为空就 return 另一棵树」。
 *    根节点上挂着 ``sc-in`` 入场动画，节点一换动画就重播，而且第二次的
 *    ``animationDelay`` 从数据到达时刻起算，各卡本来排好的阶梯彻底乱掉。
 *
 * C. **脏状态要冒泡。** 每张卡都 ``useReportDirty`` 上报，页头徽标与左侧导航
 *    据此提示「还有 N 项没保存」——否则用户滚到第 6 张卡就看不见第 3 张的改动了。
 */
import { useState, type ReactNode } from 'react';
import { App, Button, Input, Popconfirm, Segmented, Select, Switch, Tooltip } from 'antd';
import {
  CloseOutlined, EditOutlined, RollbackOutlined, SaveOutlined, UndoOutlined,
} from '@ant-design/icons';

import JsonEditor from '@components/JsonEditor';
import KvTableEditor from '@components/KvTableEditor';
import {
  BazelPresetsVo, EvaluatorMetricAggVo, EvaluatorWhitelistVo, ModuleRegistry, ModuleRegistryVo,
  OutputBucketRoutingVo,
  getBazelPresets, getEvaluatorMetricAgg, getEvaluatorWhitelist, getModuleRegistry,
  getOutputBucketRouting,
  resetBazelPresets, resetEvaluatorMetricAgg, resetEvaluatorWhitelist, resetModuleRegistry,
  resetOutputBucketRouting,
  updateBazelPresets, updateEvaluatorMetricAgg, updateEvaluatorWhitelist, updateModuleRegistry,
  updateOutputBucketRouting,
} from '@apis/simulation';

import {
  ConfigVo, Panel, PanelError, PanelLoading, RefreshButton, SOURCE_PILL, STAGGER_MS,
  truncateDefault, useConfigResource, useReportDirty,
} from './shared';
import {
  MAIN_REPO_MODULE, parseBranchDpModules, validateBazelPresetsJson,
  validateEvaluatorMetricAggJson, validateEvaluatorWhitelistJson, validateModuleRegistry,
  validateOutputBucketRoutingJson,
} from './validators';

/**
 * 从 axios 异常里取后端返回的中文 detail。
 *
 * 后端三张 JSON 卡的校验失败会以 4xx + ``{message}`` 返回，这句话比前端的
 * 「保存失败」有用得多。这里用逐层 ``typeof`` 窄化而不是 ``e: any``——
 * catch 变量在 strict 下本来就是 ``unknown``，标成 any 等于把整条链路的类型
 * 检查一起关掉，后面谁改了响应结构编译器也不会吭声。
 */
function readErrDetail(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const { response } = err as { response?: unknown };
  if (typeof response !== 'object' || response === null) return null;
  const { data } = response as { data?: unknown };
  if (typeof data !== 'object' || data === null) return null;
  const { message } = data as { message?: unknown };
  return typeof message === 'string' && message !== '' ? message : null;
}

/* ================================================================== *
 * 行式单值配置
 * ================================================================== */

export interface ConfigRowProps<T extends ConfigVo> {
  /** 锚点 id，左侧导航据此定位 */
  id: string;
  title: string;
  /** 一句话说明，常显（不再藏进 Popover） */
  description: ReactNode;
  placeholder: string;
  /** 入场 stagger 序号，全页唯一 */
  index: number;
  fetcher: () => Promise<T>;
  saver: (value: string) => Promise<T>;
  resetter: () => Promise<T>;
  validate: (draft: string) => string | null;
}

/**
 * 一行"Redis 优先 + 代码兜底"单值配置。
 *
 * 只读态展示当前值（等宽、可换行——长 bos:// URL 以前是 nowrap 横向滚动，
 * 只能拖着看）；点「编辑」时输入框**就地替换**只读值那一块，而不是在下面
 * 追加一行：追加会把同一张列表里后面几行整体推下去 ~41px，点第 1 行的「编辑」
 * 第 2-4 行就跟着位移，光标还没落到输入框，视线里的东西已经全动了。
 */
export function ConfigRow<T extends ConfigVo>({
  id, title, description, placeholder, index, fetcher, saver, resetter, validate,
}: ConfigRowProps<T>) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<T>({
    id,
    title,
    fetcher,
    onLoaded: (cfg) => {
      setDraft(cfg.currentValue);
      setEditing(false); // 保存 / 重置 / 刷新拿到新值后自动收起编辑态
    },
  });

  const isDirty = config != null && draft.trim() !== config.currentValue.trim();
  useReportDirty(id, isDirty);

  /**
   * 实时校验。三张 JSON 卡都有「校验不过 → 保存按钮 disable + 就地红字」，
   * 唯独这四行是「点了保存才弹一句 message.error」——那句提示几秒后自己消失，
   * 用户回头再看，输入框里还是那个错值，却不知道错在哪。
   * 保存路径里的校验保留不动（它是提交前的最后一道闸），这里只是把同一个
   * 结论提前、并且常显。
   */
  const liveError = editing && config != null ? validate(draft.trim()) : null;

  const handleSave = async () => {
    const trimmed = draft.trim();
    const err = validate(trimmed);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await saver(trimmed));
      message.success('已保存，立即对后续任务生效');
    } catch (e) {
      message.error('保存失败，请稍后重试');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetter());
      message.success('已重置为代码默认配置');
    } catch (e) {
      message.error('重置失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  // 重置：Redis 里已经没值（回退到代码默认）时不可再重置
  const canReset = config != null && config.source !== 'code_default';

  return (
    <div
      className={`sc-row${editing ? ' editing' : ''}${isDirty ? ' dirty' : ''}`}
      id={id}
      style={{ animationDelay: `${index * STAGGER_MS}ms` }}
    >
      <div className="sc-row-main">
        <div className="sc-row-head">
          <span className="sc-row-title">{title}</span>
          {config != null && SOURCE_PILL[config.source]}
          {isDirty && <span className="sc-dirty-tag">未保存</span>}
        </div>
        <div className="sc-row-desc">{description}</div>

        {config == null ? (
          loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
        ) : (
          <>
            {/* 编辑态就地替换只读值，不额外占一行（见组件 docstring） */}
            {editing ? (
              <div className="sc-row-edit">
                <Input
                  className="sc-input"
                  size="small"
                  autoFocus
                  value={draft}
                  placeholder={placeholder}
                  onChange={(e) => setDraft(e.target.value)}
                  onPressEnter={handleSave}
                />
                <Tooltip title="保存">
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={!isDirty || liveError != null}
                  />
                </Tooltip>
                <Tooltip title="撤销本地改动">
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={() => setDraft(config.currentValue)}
                    disabled={!isDirty}
                  />
                </Tooltip>
              </div>
            ) : (
              <div className={`sc-value${config.currentValue ? '' : ' empty'}`}>
                {config.currentValue || '（空）'}
              </div>
            )}

            {liveError != null && <div className="sc-row-error">{liveError}</div>}

            {config.defaultValue && (
              <div className="sc-meta">
                <span className="sc-def">
                  默认
                  <code title={config.defaultValue}>{config.defaultValue}</code>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {config != null && (
        <div className="sc-row-actions">
          {editing ? (
            <Tooltip title="收起编辑（不保存）">
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => { setDraft(config.currentValue); setEditing(false); }}
              />
            </Tooltip>
          ) : (
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(true)}>
              编辑
            </Button>
          )}
          <RefreshButton onClick={fetchConfig} loading={loading} />
          <Popconfirm
            title="重置为代码默认值？"
            description="将清除 Redis 配置，下游回退到代码兜底值。"
            okText="确认重置"
            cancelText="取消"
            onConfirm={handleReset}
            disabled={!canReset}
          >
            <Tooltip title="重置为代码默认">
              <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
            </Tooltip>
          </Popconfirm>
        </div>
      )}
    </div>
  );
}

/* ================================================================== *
 * 行式布尔开关
 * ================================================================== */

export interface ConfigSwitchRowProps<T extends ConfigVo> {
  /** 锚点 id，左侧导航据此定位 */
  id: string;
  title: string;
  /** 一句话说明，常显 */
  description: ReactNode;
  /** 开关打开时右侧的即时说明 */
  onText: string;
  /** 开关关闭时右侧的即时说明——只有调用方知道关掉会发生什么，所以由它给 */
  offText: string;
  /** 入场 stagger 序号，全页唯一 */
  index: number;
  fetcher: () => Promise<T>;
  saver: (value: string) => Promise<T>;
  resetter: () => Promise<T>;
  validate: (draft: string) => string | null;
}

/** VO 里的布尔文本 → 开关态。只认 ``true``（与后端 fail-open 口径一致）。 */
const _isOn = (raw: string): boolean => (raw || '').trim().toLowerCase() === 'true';

/**
 * 一行布尔开关配置（value_format='bool'）。
 *
 * 与 ``ConfigRow`` 的三点差异，都是「布尔值没有中间态」推出来的：
 *
 * 1. **没有「编辑」这一步。** 单值配置要先点「编辑」是为了不让输入框常驻；
 *    开关本身就是控件，再套一层编辑态等于「点编辑 → 再点开关」两次动作。
 *    开关直接改 draft。
 * 2. **改动不立即生效，仍要按「保存」。** 这是刻意的：本页是生产运维页，
 *    旁边每一项都是「改草稿 → 保存」，开关若一拨就落库，用户拨完就走会以为
 *    已经生效（开关是「关闭预测数据发放」这种有副作用的动作）。未保存时行内
 *    ``未保存`` 标签 + 左侧导航状态点 + 页头徽标三处同时提示，并给出保存/撤销。
 * 3. **展示的是语义而不是 ``true``/``false``。** 值存的是布尔文本，页面上给的是
 *    「开放 / 停发」和一句后果说明——运维看的是「现在发不发预测数据」，
 *    不是这个字段的字面量。
 */
export function ConfigSwitchRow<T extends ConfigVo>({
  id, title, description, onText, offText, index, fetcher, saver, resetter, validate,
}: ConfigSwitchRowProps<T>) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<T>({
    id,
    title,
    fetcher,
    onLoaded: (cfg) => setDraft(_isOn(cfg.currentValue)),
  });

  const current = config != null && _isOn(config.currentValue);
  const isDirty = config != null && draft !== current;
  useReportDirty(id, isDirty);

  /** 开关态 → 提交给后端的布尔文本。与 ``validate`` 的口径一致。 */
  const draftText = draft ? 'true' : 'false';
  const liveError = isDirty ? validate(draftText) : null;

  const handleSave = async () => {
    const err = validate(draftText);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await saver(draftText));
      message.success('已保存，立即对后续任务生效');
    } catch (e) {
      message.error('保存失败，请稍后重试');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetter());
      message.success('已重置为出厂默认');
    } catch (e) {
      message.error('重置失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  const canReset = config != null && config.source !== 'code_default';

  return (
    <div
      className={`sc-row${isDirty ? ' dirty' : ''}`}
      id={id}
      style={{ animationDelay: `${index * STAGGER_MS}ms` }}
    >
      <div className="sc-row-main">
        <div className="sc-row-head">
          <span className="sc-row-title">{title}</span>
          {config != null && SOURCE_PILL[config.source]}
          {isDirty && <span className="sc-dirty-tag">未保存</span>}
        </div>
        <div className="sc-row-desc">{description}</div>

        {config == null ? (
          loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
        ) : (
          <>
            <div className="sc-switch-line">
              <Switch
                checked={draft}
                loading={saving}
                onChange={setDraft}
                checkedChildren="开放"
                unCheckedChildren="停发"
              />
              {/* 关掉是「会改变仿真取数」的方向，用 warn 色常显后果；开着用普通灰 */}
              <span className={`sc-switch-hint${draft ? '' : ' off'}`}>
                {draft ? onText : offText}
              </span>
            </div>

            {liveError != null && <div className="sc-row-error">{liveError}</div>}

            {config.defaultValue && (
              <div className="sc-meta">
                <span className="sc-def">
                  默认
                  <code title={config.defaultValue}>
                    {_isOn(config.defaultValue) ? '开放' : '停发'}
                  </code>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {config != null && (
        <div className="sc-row-actions">
          {isDirty && (
            <>
              <Tooltip title="保存">
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={liveError != null}
                />
              </Tooltip>
              <Tooltip title="撤销本地改动">
                <Button size="small" icon={<UndoOutlined />} onClick={() => setDraft(current)} />
              </Tooltip>
            </>
          )}
          <RefreshButton onClick={fetchConfig} loading={loading} />
          <Popconfirm
            title="重置为出厂默认？"
            description="恢复为「开放」，下游立即按开放取预测数据。"
            okText="确认重置"
            cancelText="取消"
            onConfirm={handleReset}
            disabled={!canReset}
          >
            <Tooltip title="重置为出厂默认">
              <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
            </Tooltip>
          </Popconfirm>
        </div>
      )}
    </div>
  );
}

/* ================================================================== *
 * 行式枚举下拉
 * ================================================================== */

export interface ConfigSelectOption {
  /** 存库的字面量（后端只认这一组值，前端不另造语义） */
  value: string;
  /** 下拉里展示的文字 */
  label: string;
  /** 选中后紧挨控件的一句后果说明（对齐 ``ConfigSwitchRow`` 的 onText/offText） */
  hint: string;
  /**
   * 该选项是不是「回落/降级」方向（解析不出、取不到时的落点）。
   * 只有调用方知道后果，所以由它标；标了就用 ops 色常显，跟开关行的「停发」一致。
   */
  warn?: boolean;
}

export interface ConfigSelectRowProps<T extends ConfigVo> {
  /** 锚点 id，左侧导航据此定位 */
  id: string;
  title: string;
  /** 一句话说明，常显 */
  description: ReactNode;
  /** 可选值。**必须**覆盖后端全部合法取值——这里不提供自由输入，避免写进脏值 */
  options: ConfigSelectOption[];
  /** 入场 stagger 序号，全页唯一 */
  index: number;
  fetcher: () => Promise<T>;
  saver: (value: string) => Promise<T>;
  resetter: () => Promise<T>;
  validate: (draft: string) => string | null;
}

/**
 * 一行枚举配置（值域固定的一组字面量，如 ``default`` / ``latest``）。
 *
 * 交互与 ``ConfigSwitchRow`` 同款、与 ``ConfigRow`` 有别：**没有「编辑」这一步**，
 * 下拉直接改 draft——先点编辑再展开下拉是两次动作，而值域是封闭的，选完即知对错。
 * 但仍要按「保存」才落库：本页是生产运维页，旁边每一项都是「改草稿 → 保存」，
 * 选完即生效会让运维选完就走、以为还没提交。
 *
 * 展示的是**后果**而不是字面量：值存 ``latest``，页面上给的是「该 tag 当前生效
 * 的版本（没有则回落 default）」——运维关心的是「新任务会吃到哪份预测数据」。
 */
export function ConfigSelectRow<T extends ConfigVo>({
  id, title, description, options, index, fetcher, saver, resetter, validate,
}: ConfigSelectRowProps<T>) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<T>({
    id,
    title,
    fetcher,
    onLoaded: (cfg) => setDraft(cfg.currentValue.trim()),
  });

  const current = config?.currentValue.trim() ?? '';
  const isDirty = config != null && draft !== current;
  useReportDirty(id, isDirty);

  const liveError = isDirty ? validate(draft) : null;

  /**
   * 值 → 选项。库里要是存了不在 ``options`` 里的历史值（比如早期手工改过 Redis），
   * 不能让它在下拉里显示成空白——那样运维会以为是「没配」。合成一个选项把原值
   * 原样显出来并标明「不在可选范围内」，让他知道该改。
   */
  const optionOf = (value: string): ConfigSelectOption => (
    options.find((o) => o.value === value)
    ?? {
      value,
      label: value || '（空）',
      hint: '当前值不在可选范围内，请选一个合法值后保存',
      warn: true,
    }
  );
  const active = optionOf(draft);

  const handleSave = async () => {
    const err = validate(draft);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await saver(draft));
      message.success('已保存，立即对后续任务生效');
    } catch (e) {
      message.error('保存失败，请稍后重试');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetter());
      message.success('已重置为出厂默认');
    } catch (e) {
      message.error('重置失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  const canReset = config != null && config.source !== 'code_default';
  const defaultOption = config?.defaultValue ? optionOf(config.defaultValue.trim()) : null;

  return (
    <div
      className={`sc-row${isDirty ? ' dirty' : ''}`}
      id={id}
      style={{ animationDelay: `${index * STAGGER_MS}ms` }}
    >
      <div className="sc-row-main">
        <div className="sc-row-head">
          <span className="sc-row-title">{title}</span>
          {config != null && SOURCE_PILL[config.source]}
          {isDirty && <span className="sc-dirty-tag">未保存</span>}
        </div>
        <div className="sc-row-desc">{description}</div>

        {config == null ? (
          loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
        ) : (
          <>
            <div className="sc-select-line">
              <Select
                className="sc-select"
                size="small"
                value={draft}
                onChange={setDraft}
                loading={saving}
                // 值域封闭：不给自由输入、不给清除（清空 = 写一个后端不认的空串）
                options={options.map((o) => ({ value: o.value, label: o.label }))}
              />
              <span className={`sc-switch-hint${active.warn ? ' off' : ''}`}>{active.hint}</span>
            </div>

            {liveError != null && <div className="sc-row-error">{liveError}</div>}

            {defaultOption && (
              <div className="sc-meta">
                <span className="sc-def">
                  默认
                  <code title={config?.defaultValue}>{defaultOption.label}</code>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {config != null && (
        <div className="sc-row-actions">
          {isDirty && (
            <>
              <Tooltip title="保存">
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={liveError != null}
                />
              </Tooltip>
              <Tooltip title="撤销本地改动">
                <Button size="small" icon={<UndoOutlined />} onClick={() => setDraft(current)} />
              </Tooltip>
            </>
          )}
          <RefreshButton onClick={fetchConfig} loading={loading} />
          <Popconfirm
            title="重置为出厂默认？"
            description={
              defaultOption
                ? `恢复为「${defaultOption.label}」，下游按它解析新任务。`
                : '将清除 Redis 配置，下游回退到代码兜底值。'
            }
            okText="确认重置"
            cancelText="取消"
            onConfirm={handleReset}
            disabled={!canReset}
          >
            <Tooltip title="重置为出厂默认">
              <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
            </Tooltip>
          </Popconfirm>
        </div>
      )}
    </div>
  );
}

/* ================================================================== *
 * 双视图切换器
 * ================================================================== */

type View = 'form' | 'json';

/** 面板右上角的「表单 / JSON」切换。 */
function ViewSwitch({ value, onChange }: { value: View; onChange: (v: View) => void }) {
  return (
    <Segmented
      size="small"
      value={value}
      onChange={(v) => onChange(v as View)}
      options={[{ label: '表单', value: 'form' }, { label: 'JSON', value: 'json' }]}
    />
  );
}

/**
 * 三张 JSON 卡的 JsonEditor 高度档位。
 *
 * 不传 minRows / maxRows 时组件默认 14 / 34 行 ≈ 最小 300px、最大 700px——
 * 1366×768 这类屏上光一个编辑器就吃掉整屏，工具条被挤到折叠线以下，
 * 用户改完得先滚下去才找得到「保存」。按各卡真实内容量给档。
 */
const EDITOR_ROWS = {
  /** bazel 预设：通常 2-3 条，每条 4 行 */
  presets: { minRows: 8, maxRows: 20 },
  /** module_registry：paths 二十来个模块 + 两个长串 */
  registry: { minRows: 10, maxRows: 24 },
  /** 评测器白名单：30+ 条一行一个 */
  whitelist: { minRows: 10, maxRows: 26 },
  /** 评估器参数聚合声明：嵌套 spec，篇幅较大 */
  metricAgg: { minRows: 12, maxRows: 30 },
} as const;

/* ================================================================== *
 * bazel 编译预设
 * ================================================================== */

/**
 * bazel 一键填版本预设编辑卡（管理员专用）。
 *
 * 值是 ``{"<key>": {label, branchDpInfo, baseVersion}}`` 的嵌套字典，
 * 因此表单视图用 ``KvTableEditor`` 的 nested 形态逐条编辑，
 * JSON 视图给需要整体粘贴 / diff 的场景。
 */
export function BazelPresetsCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState('');
  const [view, setView] = useState<View>('form');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<BazelPresetsVo>({
    id: 'sec-bazel-presets',
    title: 'bazel 预设',
    fetcher: getBazelPresets,
    onLoaded: (cfg) => setDraft(cfg.currentValue),
  });

  const isDirty = config != null && draft.trim() !== config.currentValue.trim();
  useReportDirty('sec-bazel-presets', isDirty);

  const handleSave = async () => {
    const trimmed = draft.trim();
    const err = validateBazelPresetsJson(trimmed);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await updateBazelPresets(trimmed));
      message.success('已保存，立即对后续创建任务生效');
    } catch (e) {
      // 后端校验失败时透传 detail（4xx）；其它异常给通用提示
      message.error(readErrDetail(e) ?? '保存失败，请检查 JSON 格式');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetBazelPresets());
      message.success('已重置为代码默认预设');
    } catch (e) {
      message.error('重置失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  const info = (
    <div>
      <p>
        JSON 字典结构：<code>{'{"<key>": {"label", "branchDpInfo", "baseVersion"}, ...}'}</code>
      </p>
      <ul>
        <li><code>key</code>：稳定 ID，用作下拉 value 与版本侦测正则源（如 <code>10.10</code>）</li>
        <li><code>label</code>：下拉显示文案</li>
        <li><code>branchDpInfo</code>：textarea 多行 <code>module branch</code> 对</li>
        <li><code>baseVersion</code>：基线 tag 占位（用户可改）</li>
      </ul>
      <p>
        修改后立即对 CreateJobModal / CreateSceneQcJobModal 的「一键填版本」下拉生效。
      </p>
    </div>
  );

  const canReset = config != null && config.source !== 'code_default';
  // 校验不过时直接禁掉保存，不等点了才报。
  // 表单视图也要拦：KvTableEditor 里空 key / 重复 key 同样会被后端 400 顶回来。
  const blockedByJson = view === 'json' && jsonError != null;
  const blockedByForm = view === 'form' && formError != null;
  const activeError = blockedByJson ? jsonError : (blockedByForm ? formError : null);

  return (
    <Panel
      variant="cfg"
      kicker="BAZEL · 一键填版本"
      title="bazel 编译预设"
      subtitle="「一键填版本」下拉的数据源；保存后立即对后续创建任务生效。"
      id="sec-bazel-presets"
      index={index}
      dirty={isDirty}
      info={info}
      tools={(
        <>
          {config != null && <ViewSwitch value={view} onChange={setView} />}
          <RefreshButton onClick={fetchConfig} loading={loading} />
        </>
      )}
    >
      {config == null ? (
        loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
      ) : (
        <>
          <div className="sc-meta">
            {SOURCE_PILL[config.source]}
            <span className="sc-def">
              默认
              <code title={config.defaultValue}>{truncateDefault(config.defaultValue)}</code>
            </span>
          </div>

          <div className="sc-view-body">
            {view === 'form' ? (
              <KvTableEditor
                value={draft}
                onChange={setDraft}
                onValidityChange={setFormError}
                shape="nested"
                keyTitle="预设 key"
                keyPlaceholder="10.10"
                addText="新增预设"
                emptyHint="还没有预设，点「新增预设」添加一条"
                fields={[
                  { name: 'label', title: '下拉显示文案', placeholder: '10.10', flex: 1 },
                  {
                    name: 'branchDpInfo',
                    title: 'branchDpInfo（一行一对 module branch）',
                    placeholder: 'cyber orin_dev',
                    multiline: true,
                    mono: true,
                    flex: 2,
                  },
                  { name: 'baseVersion', title: '基线 tag', placeholder: '20260609.release.10.10.8', mono: true, flex: 1 },
                ]}
              />
            ) : (
              <JsonEditor
                value={draft}
                onChange={setDraft}
                validate={(_parsed, raw) => validateBazelPresetsJson(raw)}
                onValidityChange={setJsonError}
                onSave={handleSave}
                minRows={EDITOR_ROWS.presets.minRows}
                maxRows={EDITOR_ROWS.presets.maxRows}
                placeholder='{"10.10": {"label": "10.10", "branchDpInfo": "...", "baseVersion": "..."}}'
              />
            )}
          </div>

          <div className="sc-editor">
            <Tooltip title="保存（会先在本地做 JSON 校验，再交后端二次校验）">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={!isDirty || blockedByJson || blockedByForm}
              >
                保存
              </Button>
            </Tooltip>
            <Tooltip title="撤销本地改动">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => setDraft(config.currentValue)}
                disabled={!isDirty}
              />
            </Tooltip>
            <Popconfirm
              title="重置为代码默认预设？"
              description="将清除 Redis 配置，下游回退到代码内置的 10.10 / 10.9 两条预设。"
              okText="确认重置"
              cancelText="取消"
              onConfirm={handleReset}
              disabled={!canReset}
            >
              <Tooltip title="重置为代码默认">
                <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
              </Tooltip>
            </Popconfirm>
            {activeError != null && <span className="sc-editor-hint bad">{activeError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}

/* ================================================================== *
 * bazel 模块注册表
 * ================================================================== */

/**
 * 「是普通 JSON 对象」的类型守卫。``typeof null === 'object'`` 与数组都要排除，
 * 这两条是 JSON 处理里最经典的两个坑，写在一处比在三个调用点各写一遍可靠。
 */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** 把三字段拼成后端要的单个 JSON（与原实现同样 2 空格缩进）。 */
function composeRegistry(pathsJson: string, branchDpInfo: string, changeModule: string): string {
  return JSON.stringify(
    {
      paths: JSON.parse(pathsJson.trim()),
      branchDpInfo: branchDpInfo.trim(),
      changeModule: changeModule.trim(),
    },
    null,
    2,
  );
}

/** module_registry 的三个必填字段，缺一个后端 ``_validate_module_registry`` 就 400。 */
const REGISTRY_FIELDS = ['paths', 'branchDpInfo', 'changeModule'] as const;

/**
 * bazel 模块注册表编辑卡（管理员专用）。
 *
 * 一张卡里三个字段同编同存（后端单键 ``sysconfig:bazel:module_registry``，
 * 一次 PUT 原子写入），避免迁移前"paths 在 Redis、branchDpInfo/changeModule 在
 * yaml 三处重复"改一处漏一处的静默漂移：
 * - ``paths``        模块短名 → GitLab full path，供 ``git ls-remote`` 拼 URL；
 * - ``branchDpInfo`` daily / PE / PP 三条链共用的 ``模块:分支`` 串；
 * - ``changeModule`` 参与编译的模块名串（比 branchDpInfo 少一个主仓 neolix-repo）。
 *
 * 保存前跑与后端同口径的跨字段校验 ``changeModule ⊆ branchDpInfo ⊆ paths``，
 * 把"模块漏配 GitLab 路径"从编译期的 namespace not found 提前到点保存那一刻。
 *
 * 视图有两个：「表单」按三字段分别编辑（paths 走 KvTableEditor），
 * 「JSON」直接编辑合成后的 ``{paths, branchDpInfo, changeModule}`` 整体——
 * 后者给整段粘贴 / 与别人对 diff 的场景，切回表单时自动拆回三字段。
 *
 * **JSON 视图的原文由 ``jsonDraft`` 自己持有**（文件头约定 A）。三字段是
 * 「已经成功解析出来的语义值」，jsonDraft 是「用户正在敲的原文」，两者必须
 * 分开存：合起来存就意味着「原文必须时刻能被解析」，而打字过程中它绝大多数
 * 时刻都不能。``jsonSynced`` 记录两者当前是否一致，不一致时禁掉保存——
 * 否则点保存会把三字段里的**旧值**写进 Redis 并弹一句「已保存」。
 */
export function ModuleRegistryCard({ index }: { index: number }) {
  const { message, modal } = App.useApp();
  const [pathsDraft, setPathsDraft] = useState('');
  const [branchDraft, setBranchDraft] = useState('');
  const [changeDraft, setChangeDraft] = useState('');
  /** JSON 视图的编辑原文；受控 textarea 的唯一 value 来源 */
  const [jsonDraft, setJsonDraft] = useState('');
  /** jsonDraft 是否已成功回写到三字段。false = 用户敲到一半 / 结构不对 */
  const [jsonSynced, setJsonSynced] = useState(true);
  const [view, setView] = useState<View>('form');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  /**
   * 把一份 module_registry 原文铺到三字段 + JSON 原文。
   * 首次加载、刷新、「撤销本地改动」三条路径共用，保证三处语义完全一致
   * （以前撤销走的是 ``applyConfig(config)``，会额外触发一次 onLoaded 副作用，
   * 和另外两张卡的 ``setDraft(config.currentValue)`` 不是一回事）。
   *
   * 后端保证三字段存在且类型正确（损坏时已回退代码默认），这里再兜一层：
   * 解析失败就把三个框留空而不是整卡崩掉，管理员仍能重新填写并保存覆盖。
   */
  const loadDrafts = (raw: string) => {
    try {
      const obj = JSON.parse(raw) as ModuleRegistry;
      // 运行时拿到的是任意 JSON，别信静态类型：逐个 typeof 兜底，
      // 免得把数字/对象塞进 textarea 的 value 造成受控组件报警
      const paths = JSON.stringify(obj.paths ?? {}, null, 2);
      const branch = typeof obj.branchDpInfo === 'string' ? obj.branchDpInfo : '';
      const change = typeof obj.changeModule === 'string' ? obj.changeModule : '';
      setPathsDraft(paths);
      setBranchDraft(branch);
      setChangeDraft(change);
      setJsonDraft(composeRegistry(paths, branch, change));
      setJsonSynced(true);
    } catch (e) {
      console.error('module_registry JSON 解析失败', e);
      setPathsDraft('');
      setBranchDraft('');
      setChangeDraft('');
      setJsonDraft('');
      setJsonSynced(true); // 三个空框与空原文本来就是一致的
      message.warning('当前配置不是合法 JSON，请重新填写后保存覆盖');
    }
  };

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<ModuleRegistryVo>({
    id: 'sec-module-registry',
    title: 'module_registry',
    fetcher: getModuleRegistry,
    onLoaded: (cfg) => loadDrafts(cfg.currentValue),
  });

  // 三字段任一改动都算脏。与 currentValue 逐字符比不可靠（缩进会变），
  // 改为按"解析后的语义值"比对。
  const isDirty = (() => {
    if (config == null) return false;
    try {
      const cur = JSON.parse(config.currentValue) as ModuleRegistry;
      const curPaths = JSON.stringify(cur.paths ?? {}, null, 2);
      return (
        pathsDraft.trim() !== curPaths.trim() ||
        branchDraft.trim() !== (cur.branchDpInfo ?? '').trim() ||
        changeDraft.trim() !== (cur.changeModule ?? '').trim()
      );
    } catch {
      return true;
    }
  })();
  useReportDirty('sec-module-registry', isDirty);

  /**
   * 用当前三字段合成 JSON 原文。paths 不是合法 JSON 时退化成一个 paths 为空的壳，
   * 让用户能在 JSON 视图里把 paths 重新写对（而不是卡在「切不过去」）。
   */
  const composeForView = (): string => {
    try {
      return composeRegistry(pathsDraft, branchDraft, changeDraft);
    } catch {
      return JSON.stringify(
        { paths: {}, branchDpInfo: branchDraft.trim(), changeModule: changeDraft.trim() },
        null,
        2,
      );
    }
  };

  /**
   * JSON 视图 onChange：**先无条件回存原文**（否则受控 textarea 会把刚敲的字回滚），
   * 再尝试解析回写三字段。解析失败只标记 unsynced，不动三字段。
   *
   * 字段缺失时保留三字段当前值而不是清空：以前 ``obj.paths ?? {}`` /
   * ``typeof obj.branchDpInfo === 'string' ? … : ''`` 会在用户删掉某个 key 的
   * 瞬间把对应字段悄悄清空，切回表单一看东西没了。缺字段是**校验错误**
   * （后端 ``_validate_module_registry`` 直接 400），交给 validate 显式报出来。
   */
  const handleJsonDraftChange = (next: string) => {
    setJsonDraft(next);
    try {
      const parsed: unknown = JSON.parse(next);
      if (isPlainObject(parsed)) {
        // 只回写「类型也对」的字段。类型不对的（paths 写成数组、branchDpInfo 写成
        // 数字）留给下面的 validate 报错，别把垃圾值塞进表单视图的输入框
        const paths = parsed.paths;
        const branch = parsed.branchDpInfo;
        const change = parsed.changeModule;
        let ok = true;
        if (isPlainObject(paths)) setPathsDraft(JSON.stringify(paths, null, 2));
        else ok = false;
        if (typeof branch === 'string') setBranchDraft(branch);
        else ok = false;
        if (typeof change === 'string') setChangeDraft(change);
        else ok = false;
        setJsonSynced(ok);
        return;
      }
    } catch {
      // 半成品 JSON（用户正在打字），保留原文、不动三字段
    }
    setJsonSynced(false);
  };

  /**
   * 切视图。
   * - → JSON：用当前三字段重新合成原文（表单侧的改动必须带过去）；
   * - → 表单：若原文还没能解析回三字段，切过去等于**静默丢弃**这次编辑，
   *   必须先问一句。以前这里是无声的，用户改了半天 JSON 一切回来全没了。
   */
  const handleViewChange = (next: View) => {
    if (next === view) return;
    if (next === 'json') {
      setJsonDraft(composeForView());
      setJsonSynced(true);
      setView('json');
      return;
    }
    if (!jsonSynced) {
      modal.confirm({
        title: 'JSON 还不能解析成三个字段',
        content: '切回表单视图会丢弃这次编辑，三个字段回到上一次成功同步的内容。',
        okText: '仍然切回表单',
        cancelText: '留在 JSON 继续改',
        okButtonProps: { danger: true },
        onOk: () => {
          setJsonDraft(composeForView());
          setJsonSynced(true);
          setView('form');
        },
      });
      return;
    }
    setView('form');
  };

  /** 从 branchDpInfo 派生 changeModule：去重保序后剥掉主仓 neolix-repo */
  const handleDerive = () => {
    let modules: string[];
    try {
      modules = parseBranchDpModules(branchDraft);
    } catch (e) {
      message.error((e as Error).message);
      return;
    }
    if (modules.length === 0) {
      message.error('branchDpInfo 为空，无法派生');
      return;
    }
    // 主仓不是编译模块（后端 _strip_main_repo_from_change_module 运行时也会再剥一次）
    const derived = modules.filter((m) => m !== MAIN_REPO_MODULE).join(',');
    setChangeDraft(derived);
    // JSON 视图下同步原文，否则编辑器里还显示派生前的 changeModule
    if (view === 'json') {
      try {
        setJsonDraft(composeRegistry(pathsDraft, branchDraft, derived));
        setJsonSynced(true);
      } catch {
        // paths 当前不合法，保持原文不动，用户改完 paths 自会重新同步
      }
    }
    message.success('已从 branchDpInfo 派生 changeModule（已剥除主仓）');
  };

  const handleSave = async () => {
    const err = validateModuleRegistry(pathsDraft, branchDraft, changeDraft);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await updateModuleRegistry(composeRegistry(pathsDraft, branchDraft, changeDraft)));
      message.success('已保存，下一次 daily / PE / PP 创建与 bazel 编译立即生效');
    } catch (e) {
      message.error(readErrDetail(e) ?? '保存失败，请检查格式');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetModuleRegistry());
      message.success('已重置：回退到代码内置默认注册表');
    } catch (e) {
      message.error('重置失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  const info = (
    <div>
      <p>三个字段同存一个 Redis 键，一次保存原子写入：</p>
      <ul>
        <li>
          <code>paths</code>：<code>{'{"<short_name>": "<gitlab_full_path>"}'}</code>，
          resolver 拼进 <code>git ls-remote</code> URL
        </li>
        <li>
          <code>branchDpInfo</code>：<code>模块:分支</code> 逗号串，daily / PE / PP 共用
        </li>
        <li>
          <code>changeModule</code>：参与编译的模块名逗号串（不含主仓 <code>neolix-repo</code>）
        </li>
      </ul>
      <p>
        保存时强制 <code>changeModule ⊆ branchDpInfo ⊆ paths</code>：branchDpInfo 里
        出现 paths 未登记的模块会直接报错并点名，不用等编译时 GitLab
        namespace not found。Redis 优先 + 代码内置默认兜底，修改后立即生效，无需重启 pod。
      </p>
    </div>
  );

  // 重置：Redis 里已经没值时 disable
  const canReset = config != null && config.source !== 'code_default';
  // JSON 视图：语法/结构不合法，或原文还没回写进三字段，都不许保存
  const blockedByJson = view === 'json' && (jsonError != null || !jsonSynced);
  const blockedByForm = view === 'form' && formError != null;
  const activeError = view === 'json'
    ? (jsonError ?? (jsonSynced ? null : 'JSON 尚未解析成三个字段，保存会写回旧值，已暂时禁用'))
    : formError;

  return (
    <Panel
      variant="cfg"
      kicker="BAZEL · 模块注册表"
      title="模块清单（paths + 分支 + 编译模块）"
      subtitle="三字段同存一个 Redis 键、一次保存原子写入，daily / PE / PP 三条链共用。"
      id="sec-module-registry"
      index={index}
      dirty={isDirty}
      info={info}
      tools={(
        <>
          {config != null && <ViewSwitch value={view} onChange={handleViewChange} />}
          <RefreshButton onClick={fetchConfig} loading={loading} />
        </>
      )}
    >
      {config == null ? (
        loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
      ) : (
        <>
          <div className="sc-meta">{SOURCE_PILL[config.source]}</div>

          {view === 'form' ? (
            <>
              <div className="sc-field-label"><code>paths</code> — 模块短名 → GitLab full path</div>
              <KvTableEditor
                value={pathsDraft}
                onChange={setPathsDraft}
                onValidityChange={setFormError}
                shape="flat"
                keyTitle="模块短名"
                keyPlaceholder="cyber"
                addText="新增模块"
                emptyHint="还没有登记任何模块，点「新增模块」添加"
                fields={[{ name: 'path', title: 'GitLab full path', placeholder: 'neolix/system/cyber', mono: true }]}
              />

              <div className="sc-field-label"><code>branchDpInfo</code> — 模块:分支 逗号串</div>
              <Input.TextArea
                className="sc-mono-area"
                value={branchDraft}
                placeholder="cyber:orin_dev,planning:orin_dev"
                autoSize={{ minRows: 3, maxRows: 8 }}
                onChange={(e) => setBranchDraft(e.target.value)}
              />

              <div className="sc-field-label"><code>changeModule</code> — 参与编译的模块名逗号串</div>
              <Input.TextArea
                className="sc-mono-area"
                value={changeDraft}
                placeholder="cyber,planning"
                autoSize={{ minRows: 2, maxRows: 6 }}
                onChange={(e) => setChangeDraft(e.target.value)}
              />
            </>
          ) : (
            // 表单视图第一个元素是 .sc-field-label（margin-top 12px），
            // 这里必须同为 12px，否则来回切视图内容会上下跳 2px
            <div className="sc-view-body">
              <JsonEditor
                value={jsonDraft}
                onChange={handleJsonDraftChange}
                validate={(parsed) => {
                  if (!isPlainObject(parsed)) {
                    return 'module_registry 必须是 JSON 对象（非数组 / 非标量）';
                  }
                  // 缺字段不再静默补默认值：后端 _validate_module_registry 会 400，
                  // 这里必须当成校验错误显式报出来并点名是哪个字段
                  const missing = REGISTRY_FIELDS.filter((k) => parsed[k] === undefined);
                  if (missing.length > 0) {
                    return `缺少必填字段 ${missing.join(' / ')}；三个字段是一次原子写入，缺一个后端会直接拒绝`;
                  }
                  const { paths, branchDpInfo, changeModule } = parsed;
                  if (!isPlainObject(paths)) return 'paths 必须是 JSON 对象';
                  if (typeof branchDpInfo !== 'string') return 'branchDpInfo 必须是字符串';
                  if (typeof changeModule !== 'string') return 'changeModule 必须是字符串';
                  // 复用与保存路径完全同一个跨字段校验，避免两处口径漂移
                  return validateModuleRegistry(JSON.stringify(paths), branchDpInfo, changeModule);
                }}
                onValidityChange={setJsonError}
                onSave={handleSave}
                minRows={EDITOR_ROWS.registry.minRows}
                maxRows={EDITOR_ROWS.registry.maxRows}
                placeholder='{"paths": {"cyber": "neolix/system/cyber"}, "branchDpInfo": "cyber:orin_dev", "changeModule": "cyber"}'
              />
            </div>
          )}

          <div className="sc-editor">
            <Tooltip title="保存（先本地跨字段校验，再交后端二次校验）">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={!isDirty || blockedByJson || blockedByForm}
              >
                保存
              </Button>
            </Tooltip>
            {/* 「派生」固定放在工具条里。以前表单视图放这、JSON 视图放编辑器的
                extraActions，切一次视图按钮就换个位置，手要重新找 */}
            <Tooltip title={`从 branchDpInfo 派生 changeModule（去重保序，剥除主仓 ${MAIN_REPO_MODULE}）`}>
              <Button size="small" onClick={handleDerive}>派生</Button>
            </Tooltip>
            <Tooltip title="撤销本地改动">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => loadDrafts(config.currentValue)}
                disabled={!isDirty}
              />
            </Tooltip>
            <Popconfirm
              title="清空 Redis 里的模块注册表？"
              description="删除 Redis 后下游回退到代码内置默认注册表（paths + branchDpInfo + changeModule 三者一起回退）。"
              okText="确认重置"
              cancelText="取消"
              onConfirm={handleReset}
              disabled={!canReset}
            >
              <Tooltip title="删除 Redis 配置">
                <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
              </Tooltip>
            </Popconfirm>
            {activeError != null && <span className="sc-editor-hint bad">{activeError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}

/* ================================================================== *
 * 评测器白名单
 * ================================================================== */

/**
 * 白名单 value 有两种形态：旧的纯字符串（中文名）与 ``{cnName, feishuUrl}``。表格视图按后者编辑，
 * 这里把纯字符串统一升成对象；已是对象的原样保留。解析失败原样返回，交给 JSON 视图去报错。
 */
const normalizeEvaluatorWhitelist = (raw: string): string => {
  try {
    const obj = JSON.parse(raw);
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return raw;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) out[k] = typeof v === 'string' ? { cnName: v } : v;
    return JSON.stringify(out, null, 2);
  } catch {
    return raw;
  }
};

/**
 * 评测器白名单编辑卡（管理员专用）。
 *
 * - JSON 字典结构 ``{"<name>": "<cn_name>", ...}``；
 * - 前后端同口径校验，失败中文提示；
 * - 保存后立即对下一次 ``/evaluator/name-map`` 与对比页矩阵生效，无需重启 pod；
 * - 重置 = 删 Redis → 回退到代码内置默认（30+ 条与迁移前 ``EVALUATOR_NAME_CN`` 等价）。
 *
 * 默认停在表单视图：30+ 条纯 JSON 靠肉眼翻很痛苦，``KvTableEditor`` 自带的
 * 搜索框才是这张卡日常"改一条中文名"的主路径。
 */
export function EvaluatorWhitelistCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState('');
  const [view, setView] = useState<View>('form');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<EvaluatorWhitelistVo>({
    id: 'sec-evaluator-whitelist',
    title: 'evaluator_whitelist',
    fetcher: getEvaluatorWhitelist,
    onLoaded: (cfg) => setDraft(normalizeEvaluatorWhitelist(cfg.currentValue)),
  });

  const isDirty = config != null && draft.trim() !== normalizeEvaluatorWhitelist(config.currentValue).trim();
  useReportDirty('sec-evaluator-whitelist', isDirty);

  const handleSave = async () => {
    const trimmed = draft.trim();
    const err = validateEvaluatorWhitelistJson(trimmed);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await updateEvaluatorWhitelist(trimmed));
      message.success('已保存，下一次对比页矩阵/下拉立即生效');
    } catch (e) {
      message.error(readErrDetail(e) ?? '保存失败，请检查 JSON 格式');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetEvaluatorWhitelist());
      message.success('已重置：回退到代码内置默认白名单');
    } catch (e) {
      message.error('重置失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  const info = (
    <div>
      <p>
        JSON 字典结构：<code>{'{"<evaluator_name>": "<中文名>", ...}'}</code>
      </p>
      <ul>
        <li><code>key</code>：评测器英文名（snake_case，如 <code>traffic_law_evaluator</code>）</li>
        <li><code>value</code>：中文展示名（如 <code>闯红灯检测</code>），或 <code>{'{"cnName": "闯红灯检测", "feishuUrl": "https://…"}'}</code></li>
        <li><code>feishuUrl</code>：可选，评估器飞书文档链接；填了之后任务详情里该评估项下方显示「查看评估器文档」</li>
      </ul>
      <p>
        严格白名单：仅登记在此的评测器出现在对比页矩阵、下拉候选、日报中文映射里。
        算法侧上报的新评测器要先在此登记才可见。Redis 优先 + 代码内置默认兜底（30+ 条
        与迁移前 <code>EVALUATOR_NAME_CN</code> 等价）；修改后立即生效，无需重启 pod。
      </p>
    </div>
  );

  const canReset = config != null && config.source !== 'code_default';
  const blockedByJson = view === 'json' && jsonError != null;
  const blockedByForm = view === 'form' && formError != null;
  const activeError = blockedByJson ? jsonError : (blockedByForm ? formError : null);

  return (
    <Panel
      variant="cfg"
      kicker="EVALUATOR · 白名单"
      title="评测器英文名 → 中文名"
      subtitle="严格白名单：只有登记在此的评测器才出现在对比页矩阵、下拉候选与日报里。"
      id="sec-evaluator-whitelist"
      index={index}
      dirty={isDirty}
      info={info}
      tools={(
        <>
          {config != null && <ViewSwitch value={view} onChange={setView} />}
          <RefreshButton onClick={fetchConfig} loading={loading} />
        </>
      )}
    >
      {config == null ? (
        loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
      ) : (
        <>
          <div className="sc-meta">
            {SOURCE_PILL[config.source]}
            <span className="sc-def">
              默认
              {/* 这条默认值是 30+ 条的 JSON 字典，截断后剩不到一行；
                  另外两张卡都挂了 title，这里漏了，鼠标悬停什么也看不到 */}
              <code title={config.defaultValue}>{truncateDefault(config.defaultValue)}</code>
            </span>
          </div>

          <div className="sc-view-body">
            {view === 'form' ? (
              <KvTableEditor
                value={draft}
                onChange={setDraft}
                onValidityChange={setFormError}
                shape="nested"
                keyTitle="评测器英文名"
                keyPlaceholder="traffic_law_evaluator"
                addText="新增评测器"
                emptyHint="白名单为空，点「新增评测器」登记一条"
                fields={[
                  { name: 'cnName', title: '中文展示名', placeholder: '闯红灯检测' },
                  { name: 'feishuUrl', title: '飞书文档链接', placeholder: 'https://xxx.feishu.cn/wiki/...', mono: true, flex: 2 },
                ]}
              />
            ) : (
              <JsonEditor
                value={draft}
                onChange={setDraft}
                validate={(_parsed, raw) => validateEvaluatorWhitelistJson(raw)}
                onValidityChange={setJsonError}
                onSave={handleSave}
                minRows={EDITOR_ROWS.whitelist.minRows}
                maxRows={EDITOR_ROWS.whitelist.maxRows}
                placeholder='{"traffic_law_evaluator": "闯红灯检测", "wrong_way_driving_evaluator": "逆行驾驶检测"}'
              />
            )}
          </div>

          <div className="sc-editor">
            <Tooltip title="保存（先本地 JSON + 结构校验，再交后端二次校验）">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={!isDirty || blockedByJson || blockedByForm}
              >
                保存
              </Button>
            </Tooltip>
            <Tooltip title="撤销本地改动">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => setDraft(normalizeEvaluatorWhitelist(config.currentValue))}
                disabled={!isDirty}
              />
            </Tooltip>
            <Popconfirm
              title="清空 Redis 里的白名单？"
              description="删除 Redis 后下游回退到代码内置默认（30+ 条与迁移前 EVALUATOR_NAME_CN 等价）。"
              okText="确认重置"
              cancelText="取消"
              onConfirm={handleReset}
              disabled={!canReset}
            >
              <Tooltip title="删除 Redis 配置">
                <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
              </Tooltip>
            </Popconfirm>
            {activeError != null && <span className="sc-editor-hint bad">{activeError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}

/* ================================================================== *
 * 产物桶分片路由表
 * ================================================================== */

/**
 * 产物桶分片路由表编辑卡（管理员专用）。只给 JSON 视图：两个字符串数组，表单视图没收益。
 * 「回滚」不是恢复出厂：只清空 writable，buckets 登记保留（只增不减，后端同样拦）。
 */
export function OutputBucketRoutingCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<OutputBucketRoutingVo>({
    id: 'sec-output-bucket-routing',
    title: 'output_bucket_routing',
    fetcher: getOutputBucketRouting,
    onLoaded: (cfg) => setDraft(cfg.currentValue),
  });

  const isDirty = config != null && draft.trim() !== config.currentValue.trim();
  useReportDirty('sec-output-bucket-routing', isDirty);

  const defaultBucket = config?.defaultBucket ?? '';
  const validate = (raw: string) =>
    validateOutputBucketRoutingJson(raw, defaultBucket, config?.currentValue);

  const handleSave = async () => {
    const trimmed = draft.trim();
    const err = validate(trimmed);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await updateOutputBucketRouting(trimmed));
      message.success('已保存，之后下发的 task 按新 writable 分配');
    } catch (e) {
      message.error(readErrDetail(e) ?? '保存失败，请检查 JSON 格式');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetOutputBucketRouting());
      message.success('已回滚：新 task 写原桶');
    } catch (e) {
      message.error(readErrDetail(e) ?? '回滚失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  const info = (
    <div>
      <p>
        JSON 结构：<code>{'{"buckets": [...], "writable": [...]}'}</code>
      </p>
      <ul>
        <li><code>writable</code>：新 task 按 <code>task_id % N</code> 轮转写入；为空写原桶</li>
        <li><code>buckets</code>：登记过的分片桶，只能追加。停写某桶请从 writable 移除</li>
        <li><b>整体回滚请点「回滚」按钮</b>（只清空 writable），不要把 JSON 改回出厂值</li>
        <li>
          打开 writable 前：所有在用 DAG 的引擎都必须已支持 <code>SIMULATION_OUTPUT_BUCKET</code>，
          否则那些 job 的产物写原桶、平台去分片桶读，前端看不到产物
        </li>
        <li>已下发 task 的桶记在 task 上，改这里不影响历史产物的读取</li>
        <li>新桶必须先建好、配好 AK 权限并完成带宽提额，再加入 writable</li>
      </ul>
    </div>
  );

  const canReset = config != null && (() => {
    try {
      return (JSON.parse(config.currentValue).writable ?? []).length > 0;
    } catch {
      return false;
    }
  })();

  return (
    <Panel
      variant="cfg"
      kicker="BOS · 产物桶分片"
      title="产物桶分片路由表"
      subtitle="决定之后下发的 task 写入哪个产物桶；已下发 task 不受影响。"
      id="sec-output-bucket-routing"
      index={index}
      dirty={isDirty}
      info={info}
      tools={<RefreshButton onClick={fetchConfig} loading={loading} />}
    >
      {config == null ? (
        loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
      ) : (
        <>
          <div className="sc-meta">
            {SOURCE_PILL[config.source]}
            <span className="sc-def">
              原桶
              <code title={config.defaultBucket}>{config.defaultBucket}</code>
            </span>
          </div>

          <div className="sc-view-body">
            <JsonEditor
              value={draft}
              onChange={setDraft}
              validate={(_parsed, raw) => validate(raw)}
              onValidityChange={setJsonError}
              onSave={handleSave}
              minRows={6}
              maxRows={16}
              placeholder='{"buckets": ["brood-simulation-output-s00"], "writable": ["brood-simulation-output-s00"]}'
            />
          </div>

          <div className="sc-editor">
            <Tooltip title="保存（先本地校验，再交后端二次校验）">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={!isDirty || jsonError != null}
              >
                保存
              </Button>
            </Tooltip>
            <Tooltip title="撤销本地改动">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => setDraft(config.currentValue)}
                disabled={!isDirty}
              />
            </Tooltip>
            <Popconfirm
              title="回滚到原桶？"
              description="清空 writable，之后下发的 task 全部写原桶；buckets 登记保留。"
              okText="确认回滚"
              cancelText="取消"
              onConfirm={handleReset}
              disabled={!canReset}
            >
              <Tooltip title="回滚到原桶">
                <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
              </Tooltip>
            </Popconfirm>
            {jsonError != null && <span className="sc-editor-hint bad">{jsonError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}

/* ================================================================== *
 * 评估器参数·指标聚合声明
 * ================================================================== */

/**
 * 评估器参数聚合声明编辑卡（管理员专用）。
 *
 * 值是 ``{"<evaluator>": {label?, trigger_key?, sum_keys?, rates?, distribution_keys?}}``
 * 的深层嵌套结构（rates 每条又是 6 字段对象），KvTableEditor 表不下，故本卡**只给
 * JSON 视图**，不做双视图。控制「评估器参数」卡片的候选、KPI 求和项、派生率口径。
 */
export function EvaluatorMetricAggCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [draft, setDraft] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { loading, config, applyConfig, fetchConfig } = useConfigResource<EvaluatorMetricAggVo>({
    id: 'sec-evaluator-metric-agg',
    title: 'evaluator_metric_agg',
    fetcher: getEvaluatorMetricAgg,
    onLoaded: (cfg) => setDraft(cfg.currentValue),
  });

  const isDirty = config != null && draft.trim() !== config.currentValue.trim();
  useReportDirty('sec-evaluator-metric-agg', isDirty);

  const handleSave = async () => {
    const trimmed = draft.trim();
    const err = validateEvaluatorMetricAggJson(trimmed);
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      applyConfig(await updateEvaluatorMetricAgg(trimmed));
      message.success('已保存，评估器参数卡片候选与聚合口径立即生效');
    } catch (e) {
      message.error(readErrDetail(e) ?? '保存失败，请检查 JSON 格式');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetEvaluatorMetricAgg());
      message.success('已重置：回退到代码内置默认聚合声明');
    } catch (e) {
      message.error('重置失败');
      console.error(e);
    } finally {
      setResetting(false);
    }
  };

  const info = (
    <div>
      <p>
        一评估器一份 spec：<code>{'{"<evaluator>": {label?, trigger_key?, sum_keys?, rates?, distribution_keys?}}'}</code>
      </p>
      <ul>
        <li><code>sum_keys</code>：计数/可加键（3A），逐 case 恒存在，跨场景直接 ∑；每项是字符串或 <code>{'{key, label?}'}</code></li>
        <li><code>rates</code>：派生率，每条含 <code>key</code> / <code>numerator</code> / <code>denominator</code> / <code>scale</code>，口径「先 ∑分子、∑分母再相除 ×scale」，绝不对各 case 求平均</li>
        <li><code>distribution_keys</code>：分布/派生键（3B），无样本时缺席，只逐场景列，缺席渲染 —</li>
        <li><code>trigger_key</code>：该键 value==0 视为未触发，退出分布统计（计数仍 ∑）</li>
      </ul>
      <p>
        登记了 spec 的评估器才出现在「评估器参数」卡片候选里。DB 权威 + 代码内置默认
        兜底（iLQR / 换道触发 / 云端绕行成功率三份）；修改后立即热更新，无需重启 pod。
      </p>
    </div>
  );

  const canReset = config != null && config.source !== 'code_default';

  return (
    <Panel
      variant="cfg"
      kicker="EVALUATOR · 参数聚合"
      title="评估器参数聚合声明"
      subtitle="纯统计型评估器的跨场景聚合 spec：控制参数卡片候选、KPI 求和项与派生率口径。"
      id="sec-evaluator-metric-agg"
      index={index}
      dirty={isDirty}
      info={info}
      tools={<RefreshButton onClick={fetchConfig} loading={loading} />}
    >
      {config == null ? (
        loading ? <PanelLoading /> : <PanelError onRetry={fetchConfig} />
      ) : (
        <>
          <div className="sc-meta">
            {SOURCE_PILL[config.source]}
            <span className="sc-def">
              默认
              <code title={config.defaultValue}>{truncateDefault(config.defaultValue)}</code>
            </span>
          </div>

          <div className="sc-view-body">
            <JsonEditor
              value={draft}
              onChange={setDraft}
              validate={(_parsed, raw) => validateEvaluatorMetricAggJson(raw)}
              onValidityChange={setJsonError}
              onSave={handleSave}
              minRows={EDITOR_ROWS.metricAgg.minRows}
              maxRows={EDITOR_ROWS.metricAgg.maxRows}
              placeholder='{"ilqr_path_metric_evaluator": {"trigger_key": "ilqr_valid_frames", "sum_keys": [{"key": "ilqr_valid_frames", "label": "有效帧数"}], "rates": []}}'
            />
          </div>

          <div className="sc-editor">
            <Tooltip title="保存（先本地 JSON + 结构校验，再交后端二次校验）">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={!isDirty || jsonError != null}
              >
                保存
              </Button>
            </Tooltip>
            <Tooltip title="撤销本地改动">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => setDraft(config.currentValue)}
                disabled={!isDirty}
              />
            </Tooltip>
            <Popconfirm
              title="重置为代码默认聚合声明？"
              description="将 value 恢复为 seed 默认（iLQR / 换道触发 / 云端绕行成功率三份）。"
              okText="确认重置"
              cancelText="取消"
              onConfirm={handleReset}
              disabled={!canReset}
            >
              <Tooltip title="重置为代码默认">
                <Button danger size="small" icon={<RollbackOutlined />} loading={resetting} disabled={!canReset} />
              </Tooltip>
            </Popconfirm>
            {jsonError != null && <span className="sc-editor-hint bad">{jsonError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}
